import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { SSLMode, sslModeForAzureMySqlOptions } from '../consts/mysql-consts';
import { format } from 'app/shared/utils/string.util';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { AuthConstants } from 'app/services/authentication/auth-constants';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

export const azureMySQLDomain: string = AuthConstants.RESOURCE_DOMAIN_MAP[adfModelTypeMap.azureMySql.linkedServiceType];
const azureMySQLResourceTypes: string[] = ['Microsoft.DBforMySQL/servers', 'Microsoft.DBforMySQL/flexibleServers'];

const dataflowAzureMysql: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.Mysql,
    settings: adfModelTypeMap.azureMySql.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};

export const azureMySqlMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.azureMySql.linkedServiceType,
    displayName: stringResources.AzureMySql_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-azure_database_for_mysql'
    },
    isFirstClass: true,
    supportDataFlowSource: true,
    supportDataFlowSink: true,
    dataflowSource: [dataflowAzureMysql],
    dataflowSink: [dataflowAzureMysql],
    supportPE: true,
    peGroupIDs: ['mysqlServer'],
    peWellknowErrorMessages: [
        /Client with IP address .* is not allowed to connect to this MySQL server/i
    ],
    resourceTypes: azureMySQLResourceTypes,
    supportDatasetV2: true,
    tableColumnNameQuotes: ['`', '`'],
    copySourceTypes: [adfModelTypeMap.azureMySql.copySourceType],
    copySinkTypes: [adfModelTypeMap.azureMySql.copySinkType],
    category: ['Azure'],
    supportFirstClassParameterExperience: true,
    supportPreCopyScript: true,
    supportedInTrident: true,
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'AzureDatabaseForMySQL' as ResourceKind
    },
    supportOPDG: GlobalVariables.enableOPDG,
    supportCopyJobSource: true,
    supportCopyJobSink: true,
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    supportOPDGInMirror: true,
    supportVNETInMirror: true,
    controls: [
        {
            type: 'select-button-switch',
            required: true,
            isLabelVertical: true,
            options: [
                {
                    label: stringResources.ModelName_ConnectionString,
                    value: false
                },
                {
                    label: stringResources.AzureKeyVault_DisplayText,
                    value: true,
                    hideAKVOptionIfNotChosen: false
                }
            ],
            bind: 'enableAKV',
            isNotRelatedToCredential: true,
            defaultValue: false,
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'azure-key-vault',
                            enableAKVParameterization: true,
                            bind: 'referedAKV',
                            isLabelVertical: true,
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureMySql_DisplayText),
                                `Server=<server>.${azureMySQLDomain};Port=<port>;Database=<database>;UID=<username>;PWD=<password>`)
                        }
                    ]
                },
                {
                    caseValue: false,
                    children: [
                        {
                            type: 'radio-switch',
                            nested: 1,
                            label: stringResources.AccountSelectionMethodLabel,
                            description: stringResources.AzureSql_SelectMethodDescription,
                            options: azureResourceSelectMethodOptions,
                            defaultValue: '{serverName != null || parameterized ? "manual" : "azure"}',
                            isLabelVertical: true,
                            cases: [
                                {
                                    caseValue: 'azure',
                                    children: [
                                        {
                                            type: 'azure-server-database-select',
                                            bind: {
                                                serverName: 'serverName',
                                                port: 'port',
                                                databaseName: 'databaseName',
                                                resourceID: '_serverResourceID'
                                            },
                                            defaultPort: '3306',
                                            showPEStatus: true,
                                            resourceTypes: azureMySQLResourceTypes,
                                            resourceTypeLabels: {
                                                flexibleServers: stringResources.FlexibleServer,
                                                servers: stringResources.SingleServer
                                            },
                                            serverDomain: azureMySQLDomain,
                                            validators: {
                                                serverName: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                databaseName: [
                                                    { type: 'required', errorMessage: '' }
                                                ]
                                            }
                                        }
                                    ]
                                },
                                {
                                    caseValue: 'manual',
                                    children: [
                                        {
                                            type: 'parameterizable',
                                            child:
                                            {
                                                type: 'textbox',
                                                bind: 'serverName',
                                                label: stringResources.FullyServerNameLabel,
                                                required: true,
                                                placeholder: `e.g., myserver.${azureMySQLDomain}`,
                                                validators: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                isLabelVertical: true
                                            }
                                        },
                                        {
                                            type: 'parameterizable',
                                            child:
                                            {
                                                type: 'textbox',
                                                bind: 'port',
                                                label: stringResources.ModelName_Port,
                                                isLabelVertical: true,
                                                inputType: 'number',
                                                min: 0
                                            }
                                        },
                                        {
                                            type: 'parameterizable',
                                            child:
                                            {
                                                type: 'textbox',
                                                bind: 'databaseName',
                                                label: stringResources.ModelName_DatabaseName,
                                                required: true,
                                                validators: [
                                                    { type: 'required', errorMessage: '' }
                                                ],
                                                isLabelVertical: true
                                            }
                                        },
                                        {
                                            type: 'private-endpoint-status'
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            type: 'parameterizable',
                            child:
                            {
                                type: 'textbox',
                                bind: 'userName',
                                label: stringResources.ModelName_UserName,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true
                            }
                        },
                        {

                            type: 'dynamic-akv-switcher',
                            enableAKVParameterization: true,
                            hideAKVSwitcherInConnStrProps: true,
                            bind: 'password',
                            label: stringResources.ModelName_Password,
                            child: {
                                type: 'parameterizable',
                                attribute: 'password',
                                child:
                                {
                                    type: 'password',
                                    label: stringResources.ModelName_Password,
                                    required: true,
                                    isLabelVertical: true,
                                    validators: [
                                        { type: 'required', errorMessage: '' }
                                    ]
                                },
                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureMySql_DisplayText),
                                    stringResources.ModelName_Password)
                            }
                        },
                        {
                            type: 'select-switch',
                            bind: 'sslMode',
                            label: stringResources.SSL_Mode,
                            isLabelVertical: true,
                            options: sslModeForAzureMySqlOptions,
                            defaultValue: SSLMode.Preferred,
                            cases: [
                                {
                                    caseValue: SSLMode.Disabled,
                                    children: []
                                },
                                {
                                    caseValue: null,
                                    children: [
                                        {
                                            type: 'radio-switch',
                                            bind: 'useSystemTrustStore',
                                            isLabelVertical: true,
                                            options: [
                                                {
                                                    label: stringResources.ModelName_UseSystemTrustStore,
                                                    value: true
                                                },
                                                {
                                                    label: stringResources.Mysql_Not_Use_System_Trust_Store,
                                                    value: false
                                                }
                                            ],
                                            defaultValue: false
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            type: 'key-val-list',
                            bind: 'additionalFields',
                            label: stringResources.AdditionalConnectionStringFields
                        }
                    ]
                }
            ]
        }
    ]
};
